import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Badge from './Badge.jsx';
import BackOfficeSubscriptionDialog from './BackOfficeSubscriptionDialog.jsx';

// Sum all subscription rows' yearly_amount for a site (used in the table).
function siteYearly(site) {
  if (!site || !Array.isArray(site.subscription)) return 0;
  return site.subscription.reduce((t, s) => t + (Number(s.yearly_amount) || 0), 0);
}

// Earliest non-null due_date across a site's subscriptions = its "Next Charge".
function earliestDue(site) {
  if (!site || !Array.isArray(site.subscription)) return null;
  const ds = site.subscription
    .map(s => s.due_date)
    .filter(Boolean)
    .map(d => new Date(d))
    .filter(d => !Number.isNaN(d.getTime()));
  if (!ds.length) return null;
  return new Date(Math.min(...ds.map(d => d.getTime()))).toISOString().slice(0, 10);
}

function moneyFmt(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `$${n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function BackOfficeTab({ customerId, customerName, backofficeClientId, onLinked }) {
  const [linkedId, setLinkedId] = useState(backofficeClientId);
  const [clients, setClients] = useState(null);
  const [client, setClient] = useState(null);     // full Back Office client record
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savingFlag, setSavingFlag] = useState(null);  // 'active' | 'monitor' | null
  const [editingSite, setEditingSite] = useState(null);

  useEffect(() => { setLinkedId(backofficeClientId); }, [backofficeClientId]);

  // Load the picker list (only when not yet linked).
  useEffect(() => {
    if (linkedId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get('/api/backoffice/clients');
        if (!cancelled) setClients(res.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.error || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [linkedId]);

  // Auto-suggested match when picker opens: case-insensitive name equality.
  const suggested = useMemo(() => {
    if (!clients || !customerName) return null;
    const needle = customerName.trim().toLowerCase();
    return clients.find(c => c.name && c.name.trim().toLowerCase() === needle) || null;
  }, [clients, customerName]);

  const load = useCallback(async (id) => {
    if (!id) return;
    setLoading(true); setError(null);
    try {
      const [cRes, sRes] = await Promise.all([
        axios.get(`/api/backoffice/clients/${id}`),
        axios.get('/api/backoffice/sites', { params: { clientId: id } }),
      ]);
      setClient(cRes.data);
      setSites(sRes.data || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (linkedId) load(linkedId); }, [linkedId, load]);

  async function handleLink(boClientId) {
    try {
      await axios.post('/api/backoffice/link', { contact_id: customerId, backoffice_client_id: boClientId });
      setLinkedId(boClientId);
      if (onLinked) onLinked();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  }

  async function handleUnlink() {
    if (!confirm('Unlink this customer from its Back Office client?')) return;
    await handleLink(null);
    setClient(null); setSites([]);
  }

  async function toggleFlag(flag, value) {
    setSavingFlag(flag);
    try {
      const body = flag === 'active' ? { active: value } : { monitor: value };
      const res = await axios.post(`/api/backoffice/clients/${linkedId}/flags`, body);
      setClient(prev => ({ ...(prev || {}), ...res.data }));
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSavingFlag(null);
    }
  }

  // ---- not linked yet: picker UI -------------------------------------
  if (!linkedId) {
    return (
      <div className="card">
        <h3 className="card-title">Link to Back Office Client</h3>
        <p className="form-help">
          This customer isn't linked to an XSite Back Office client yet. Pick one to start managing subscriptions here.
        </p>
        {error && <p className="error">{error}</p>}
        {!clients && <p>Loading clients…</p>}
        {clients && (
          <>
            {suggested && (
              <div className="suggested-link">
                <span>Suggested match:</span>
                <strong>{suggested.name}</strong>
                <button className="btn-primary" onClick={() => handleLink(suggested.id)}>
                  Link to {suggested.name}
                </button>
              </div>
            )}
            <div className="form-field" style={{ marginTop: 'var(--space-4)' }}>
              <label className="form-field-label">Or pick manually</label>
              <select
                className="form-input"
                defaultValue=""
                onChange={(e) => e.target.value && handleLink(e.target.value)}
              >
                <option value="">— select a client —</option>
                {clients
                  .slice()
                  .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                  .map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name}{c.external_id ? ` (${c.external_id})` : ''}
                    </option>
                  ))}
              </select>
            </div>
          </>
        )}
      </div>
    );
  }

  // ---- linked: full panel --------------------------------------------
  return (
    <div className="backoffice-tab">
      {error && <p className="error">{error}</p>}
      {loading && <p>Loading…</p>}

      {client && (
        <div className="card">
          <div className="card-header-row">
            <div>
              <h3 className="card-title" style={{ margin: 0 }}>{client.name}</h3>
              <div className="cell-mono" style={{ color: 'var(--color-text-dim)' }}>
                {client.external_id || '—'} · Back Office ID {client.id}
              </div>
            </div>
            <button className="btn-secondary" onClick={handleUnlink}>Unlink</button>
          </div>
        </div>
      )}

      <div className="card" style={{ marginTop: 'var(--space-4)' }}>
        <div className="card-header-row" style={{ marginBottom: 'var(--space-3)' }}>
          <h3 className="card-title" style={{ margin: 0 }}>Sites & Subscriptions</h3>
          {client && (
            <div className="flag-row" style={{ margin: 0 }}>
              <label className="flag-cell">
                <input
                  type="checkbox"
                  disabled={savingFlag === 'active'}
                  checked={!!client.active}
                  onChange={(e) => toggleFlag('active', e.target.checked)}
                />
                <span>Active</span>
                <Badge tone={client.active ? 'green' : 'grey'}>
                  {client.active ? 'ACTIVE' : 'DEACTIVATED'}
                </Badge>
              </label>

              <label className="flag-cell">
                <input
                  type="checkbox"
                  disabled={savingFlag === 'monitor'}
                  checked={!!client.monitor}
                  onChange={(e) => toggleFlag('monitor', e.target.checked)}
                />
                <span>Monitor</span>
                <Badge tone={client.monitor ? 'teal' : 'grey'}>
                  {client.monitor ? 'ON' : 'OFF'}
                </Badge>
              </label>
            </div>
          )}
        </div>
        {sites.length === 0 && <p className="empty-tab">No sites under this client.</p>}
        {sites.length > 0 && (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Site</th>
                  <th>POS</th>
                  <th>Start (Created)</th>
                  <th>Active Subs</th>
                  <th>Next Charge</th>
                  <th>Annual Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sites.map(s => {
                  const activeCount = (s.subscription || []).filter(x => x.due_date || (Number(x.yearly_amount) || 0) > 0).length;
                  return (
                    <tr key={s.id}>
                      <td>
                        <div>{s.name || '—'}</div>
                        <div className="cell-mono" style={{ color: 'var(--color-text-dim)', fontSize: 'var(--text-small)' }}>
                          {s.email_id || ''} {s.phone_number ? `· ${s.phone_number}` : ''}
                        </div>
                      </td>
                      <td><Badge tone="blue">{s.pos || '—'}</Badge></td>
                      <td className="cell-mono">{s.created ? String(s.created).slice(0, 10) : '—'}</td>
                      <td className="cell-mono">{activeCount}</td>
                      <td className="cell-mono">{earliestDue(s) || '—'}</td>
                      <td className="cell-mono">{moneyFmt(siteYearly(s))}</td>
                      <td>
                        <button
                          className="btn-secondary"
                          onClick={() => setEditingSite({ clientId: linkedId, siteId: s.id, siteName: s.name })}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editingSite && (
        <BackOfficeSubscriptionDialog
          clientId={editingSite.clientId}
          siteId={editingSite.siteId}
          siteName={editingSite.siteName}
          onClose={() => setEditingSite(null)}
          onSaved={() => load(linkedId)}
        />
      )}
    </div>
  );
}
